// src/pages/Swap/components/SwapForm.js
import React from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { formatBalanceForDisplay, getPriceImpactClass } from '../utils/SwapUtils';

/**
 * SwapForm component handles the token selection and input/output fields
 */
const SwapForm = ({
  // Token states
  inputToken,
  outputToken,
  onInputTokenSelect,
  onOutputTokenSelect,
  
  // Balance states
  inputTokenBalance,
  outputTokenBalance,
  
  // Amount states
  amountIn,
  amountOut,
  onAmountInChange,
  onAmountOutChange,
  
  // Exchange rate states
  exchangeRate,
  priceImpact,
  isRefreshing,
  onManualRefresh,
  
  // Action states
  isLoading,
  isApproving,
  isSwapping,
  swapError,
  
  // Transaction settings
  slippage,
  
  // Functions
  onReverseTokens,
  handleMax
}) => {
  const { t } = useTranslation();
  
  // Function to handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    onAmountInChange(value);
  };

  // Function to handle output change
  const handleOutputChange = (e) => {
    const value = e.target.value;
    onAmountOutChange(value);
  };

  // Helper to safely display price impact
  const renderPriceImpact = () => {
    if (priceImpact === null || priceImpact === undefined) return null;
    
    return (
      <span className={`price-impact ${getPriceImpactClass(priceImpact)}`}>
        {t('swap.priceImpact', { value: priceImpact.toFixed(2) })}
      </span>
    );
  };

  return (
    <>
      {/* FROM section */}
      <div className="swap-section">
        <div className="section-label">
          {t('swap.from')}
          {exchangeRate && (
            <span className="exchange-rate-label">
              1 {inputToken.symbol} ≈ {exchangeRate.toFixed(6)} {outputToken.symbol}
              <button 
                className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
                onClick={onManualRefresh}
                disabled={isLoading || isRefreshing || !inputToken.symbol || !outputToken.symbol}
                title={t('swap.refreshRate')}
              >
                <FiRefreshCw className={isRefreshing ? 'rotating' : ''} size={14} />
              </button>
            </span>
          )}
        </div>
        <div className="token-select" onClick={() => onInputTokenSelect()}>
          {(inputToken.symbol && inputToken.icon) && (
            <img src={inputToken.icon} alt={inputToken.symbol} className="swap-token-icon" />
          )}
          <span>
            {inputToken.symbol
              ? `${inputToken.name} (${inputToken.symbol})`
              : t('swap.selectTokenPrompt')}
          </span>
        </div>
        
        {/* Show user's balance for the FROM token */}
        {inputToken.symbol && (
          <div className="token-balance-line">
            {t('swap.balanceLine', { value: formatBalanceForDisplay(inputTokenBalance) || '0.0000' })}
          </div>
        )}

        <div className="input-container">
          <input
            className="amount-input"
            type="number"
            placeholder={t('swap.amountPlaceholder')}
            value={amountIn}
            onChange={handleInputChange}
            disabled={isApproving || isSwapping} // MODIFIED: Removed isLoading to keep input enabled during rate fetching
          />
          <button 
            className="max-btn" 
            onClick={handleMax}
            disabled={isLoading || isApproving || isSwapping || !inputTokenBalance}
          >
            {t('swap.max')}
          </button>
        </div>
      </div>

      {/* Reverse Button */}
      <div 
        className={`reverse-section ${(!inputToken.symbol || !outputToken.symbol) ? 'disabled' : ''}`} 
        onClick={inputToken.symbol && outputToken.symbol ? onReverseTokens : undefined} 
        title={t('swap.reverseSwap')}
      >
        <img
          src="https://img.icons8.com/ios-filled/50/ffffff/sort-down.png"
          alt={t('swap.reverseSwap')}
          className="reverse-icon"
        />
      </div>

      {/* TO section */}
      <div className="swap-section">
        <div className="section-label">
          {t('swap.to')}
          {renderPriceImpact()}
        </div>
        <div className="token-select" onClick={() => onOutputTokenSelect()}>
          {(outputToken.symbol && outputToken.icon) && (
            <img src={outputToken.icon} alt={outputToken.symbol} className="swap-token-icon" />
          )}
          <span>
            {outputToken.symbol
              ? `${outputToken.name} (${outputToken.symbol})`
              : t('swap.selectTokenPrompt')}
          </span>
        </div>

        {/* Show user's balance for the TO token */}
        {outputToken.symbol && (
          <div className="token-balance-line">
            {t('swap.balanceLine', { value: formatBalanceForDisplay(outputTokenBalance) || '0.0000' })}
          </div>
        )}

        <input
          className="amount-input"
          type="number"
          placeholder={t('swap.amountPlaceholder')}
          value={amountOut}
          onChange={handleOutputChange}
          disabled={isApproving || isSwapping} // MODIFIED: Removed isLoading to keep input enabled during rate fetching
        />
      </div>

      {/* Display error message if any */}
      {swapError && (
        <div className="swap-error-message">
          {swapError}
        </div>
      )}
    </>
  );
};

export default SwapForm;